import {
  Component,
  Host,
  h,
  State,
  Event,
  EventEmitter,
  Prop,
  Listen,
  Element,
} from '@stencil/core';
import {
  Language,
  SessionRepository,
  SessionModel,
  SessionGuests,
} from '@cesare';
import { QW_ROOM_CODE, QW_ROOM_TYPE_LIST, QwRoomTypeType } from '../../index';
import {
  sessionStorageGetItem,
  sessionStorageSetItem,
} from '../../core/storage/storage.service';
import { loadingService } from '../../core/qw-loading-manager/qw-loading.service';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { combineLatest, Subscription, tap } from 'rxjs';
import { roomType$, selectedGuests$ } from '../../core/app/app';

@Component({
  tag: 'qw-room-type-list',
  styleUrl: 'qw-room-type-list.scss',
  shadow: false,
})
export class QwRoomTypeList {
  @Element() el: HTMLElement;
  @State() session: SessionModel;
  @State() qwRoomTypeList = QW_ROOM_TYPE_LIST;
  @State() isSessionLoading: boolean = false;
  @State() currentRoomType: string;
  @State() hasSessionChildrenOrInfants: boolean;
  @State() componentLoading: boolean = true;
  @State() renderTriggered: boolean = false;
  @Prop() qwRoomTypeListTypology: 'list' | 'inline' | 'select' = 'list';
  @Event() qwBasketWillBeReset: EventEmitter<void>;
  @Event() roomTypeChange: EventEmitter<object>;

  private filterByRoomListSub: Subscription;
  private selectedGuestsSub: Subscription;

  public componentWillLoad() {
    this.currentRoomType = sessionStorageGetItem('roomType');
    SessionRepository.sessionLoaded$.subscribe((session) => {
      this.session = session;
      this.hasSessionChildrenOrInfants =
        session.context.guests.children > 0 ||
        session.context.guests.infants > 0;
    });
    SessionRepository.sessionIsLoading$.subscribe(
      (isLoading) => (this.isSessionLoading = isLoading),
    );
    this.componentIsLoading$.pipe(distinctUntilChanged()).subscribe((value) => {
      this.componentLoading = value;
      this.setServiceLoading(this.componentLoading);
    });

    this.setServiceLoading(true);

    loadingService.loadComplete$.subscribe((res) => {
      this.renderTriggered = res;
    });

    this.selectedGuestsSub = selectedGuests$
      .pipe(
        tap((guests) => {
          if (this.qwRoomTypeListTypology === 'list') {
            this.hasSessionChildrenOrInfants =
              guests.children > 0 || guests.infants > 0;
          }
        }),
      )
      .subscribe();
  }

  private setServiceLoading(isLoading: boolean) {
    loadingService.setLoading({
      idComponent: 'room-list-type',
      isLoading,
    });
  }

  public componentIsLoading$ = combineLatest([
    SessionRepository.sessionIsLoading$,
    SessionRepository.sessionLoaded$,
  ]).pipe(
    map(([sessionLoading, sessionLoaded]) => {
      return sessionLoading && !sessionLoaded;
    }),
    debounceTime(0),
    distinctUntilChanged(),
  );

  disconnectedCallback() {
    this.filterByRoomListSub.unsubscribe();
    this.selectedGuestsSub.unsubscribe();
  }

  @Listen('qwGuestChange', { target: 'window' })
  public onGuestChange(e: CustomEvent<SessionGuests>) {
    if (this.qwRoomTypeListTypology === 'list') {
      this.hasSessionChildrenOrInfants =
        e.detail.children > 0 || e.detail.infants > 0;
    }
    if (
      this.hasSessionChildrenOrInfants &&
      (this.currentRoomType == QW_ROOM_CODE.Bunk ||
        this.currentRoomType == QW_ROOM_CODE.BunkF)
    ) {
      const allRoomType = QW_ROOM_TYPE_LIST[0];
      this.setRoomTypeInStorageAsFilter(allRoomType.value);
      this.roomTypeChange.emit(allRoomType);
      this.currentRoomType = allRoomType.value;
    }
  }

  public onClickRoomType(roomType?) {
    if (roomType) {
      this.setRoomTypeInStorageAsFilter(roomType.value);
      this.roomTypeChange.emit(roomType);
      this.closeComponent();

      this.currentRoomType = roomType.value;
    }
  }

  private setRoomTypeInStorageAsFilter(roomTypeValue) {
    sessionStorageSetItem('roomType', roomTypeValue);
    roomType$.next(roomTypeValue);
  }

  private isCurrentRoomType(roomType) {
    return roomType === sessionStorageGetItem('roomType');
  }

  arrayToCssClasses(array: string[]): string {
    return array.filter(Boolean).join(' ');
  }

  createRoomTypeOption(
    optionFactory: (options: {
      roomType: QwRoomTypeType;
      extraLabel: string;
      hasChildrenInDorm: boolean;
      isSelected: boolean;
    }) => string,
  ) {
    return this.qwRoomTypeList.map((r: QwRoomTypeType) => {
      const isSelected = this.isCurrentRoomType(r.value);
      const isDorm =
        r.value === QW_ROOM_CODE.Bunk || r.value === QW_ROOM_CODE.BunkF;
      const hasChildrenInDorm = this.hasSessionChildrenOrInfants && isDorm;
      const extraLabel = hasChildrenInDorm ? (
        <span class="qw-room-type-list__sublabel">
          {Language.getTranslation('guestsMustBe18yrsOrOlder')}
        </span>
      ) : (
        ''
      );
      return optionFactory({
        roomType: r,
        extraLabel,
        hasChildrenInDorm,
        isSelected,
      });
    });
  }

  selectOnChange(e) {
    const selected = this.qwRoomTypeList.find(
      (r) => r.value === e.target.value,
    );
    if (selected) {
      this.setRoomTypeInStorageAsFilter(selected.value);
      this.roomTypeChange.emit(selected);
      this.currentRoomType = selected.value;
    }
    this.closeComponent();
  }

  public closeComponent() {
    document.querySelectorAll('qw-room-type-list').forEach((list) => {
      list.classList.remove('qw-componet--show');
    });
  }

  render() {
    const hostClasses = this.arrayToCssClasses([
      this.isSessionLoading ? 'qw-room-type-list--disabled' : '',
      this.qwRoomTypeListTypology == 'inline'
        ? 'qw-room-type-list--inline'
        : '',
      !this.renderTriggered ? 'disable-room-list' : '',
      this.renderTriggered && this.componentLoading ? 'loading-room-list' : '',
    ]);

    const elementList = () => {
      return this.createRoomTypeOption(
        ({ roomType, extraLabel, hasChildrenInDorm }) => {
          const optionClasses = this.arrayToCssClasses([
            'qw-room-type-list__item',
            this.isCurrentRoomType(roomType.value)
              ? 'qw-room-type-list__item--current'
              : '',
            hasChildrenInDorm ? 'qw-room-type-list__item--disabled' : '',
          ]);
          return (
            <div
              class={optionClasses}
              onClick={() => this.onClickRoomType(roomType)}
            >
              {roomType.label}
              {extraLabel}
            </div>
          );
        },
      );
    };

    const nativeSelect = () => {
      return (
        <div>
          <select onChange={(e) => this.selectOnChange(e)}>
            {this.createRoomTypeOption(
              ({ roomType, hasChildrenInDorm, isSelected }) => (
                <option
                  selected={isSelected}
                  disabled={hasChildrenInDorm}
                  value={roomType.value}
                >
                  {roomType.label}
                </option>
              ),
            )}
          </select>
          {this.hasSessionChildrenOrInfants &&
            this.renderTriggered &&
            !this.componentLoading && (
              <span class="qw-room-type-list__sublabel--select">
                {Language.getTranslation('dormGuestsMustBe18yrsOrOlder')}
              </span>
            )}
        </div>
      );
    };

    return (
      this.qwRoomTypeList && (
        <Host class={hostClasses}>
          {this.qwRoomTypeListTypology == 'select'
            ? nativeSelect()
            : elementList()}
        </Host>
      )
    );
  }
}
